import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth, useApp } from './AppContext'
import { LayoutDashboard, ArrowLeftRight, Camera, Car, FileText, Settings, Calendar, Landmark, X, MoreHorizontal } from 'lucide-react'

/* ── Quarterly Q mark — geometric, constructed, not a font character ── */
export function QMark({ size = 34, variant = 'teal' }) {
  if (variant === 'teal') {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="10" fill="#00D4AA"/>
        <circle cx="19" cy="19" r="9.5" stroke="#08121F" strokeWidth="4" fill="none"/>
        <line x1="25.5" y1="25.5" x2="32" y2="32" stroke="#08121F" strokeWidth="4.5" strokeLinecap="round"/>
        <line x1="29.5" y1="25" x2="32.5" y2="22" stroke="#08121F" strokeWidth="3.5" strokeLinecap="round"/>
      </svg>
    )
  }
  if (variant === 'dark') {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="10" fill="#112236" stroke="#1C3347" strokeWidth="1"/>
        <circle cx="19" cy="19" r="9.5" stroke="#00D4AA" strokeWidth="4" fill="none"/>
        <line x1="25.5" y1="25.5" x2="32" y2="32" stroke="#00D4AA" strokeWidth="4.5" strokeLinecap="round"/>
        <line x1="29.5" y1="25" x2="32.5" y2="22" stroke="#00D4AA" strokeWidth="3.5" strokeLinecap="round"/>
      </svg>
    )
  }
  // icon-only for tab bar active state
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="19" cy="19" r="9.5" stroke="#00D4AA" strokeWidth="4" fill="none"/>
      <line x1="25.5" y1="25.5" x2="32" y2="32" stroke="#00D4AA" strokeWidth="4.5" strokeLinecap="round"/>
      <line x1="29.5" y1="25" x2="32.5" y2="22" stroke="#00D4AA" strokeWidth="3.5" strokeLinecap="round"/>
    </svg>
  )
}

const PRIMARY_TABS = [
  { to: '/dashboard',  icon: LayoutDashboard, label: 'Home' },
  { to: '/categorise', icon: ArrowLeftRight,  label: 'Sort',    badge: true },
  { to: '/mileage',    icon: Car,             label: 'Mileage' },
  { to: '/submit',     icon: FileText,        label: 'Submit' },
]

const MORE_ITEMS = [
  { to: '/receipts',  icon: Camera,    label: 'Receipts & VAT' },
  { to: '/timeline',  icon: Calendar,  label: 'Tax Timeline' },
  { to: '/hmrc-tax',  icon: Landmark,  label: 'HMRC Tax' },
  { to: '/settings',  icon: Settings,  label: 'Settings' },
]

const ALL_NAV = [
  { to: '/dashboard',  icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/categorise', icon: ArrowLeftRight,  label: 'Categorise', badge: true },
  { to: '/receipts',   icon: Camera,          label: 'Receipts & VAT' },
  { to: '/mileage',    icon: Car,             label: 'Mileage & Office' },
  { to: '/timeline',   icon: Calendar,        label: 'Tax Timeline' },
  { to: '/submit',     icon: FileText,        label: 'MTD Submit' },
  { to: '/hmrc-tax',   icon: Landmark,        label: 'HMRC Tax' },
  { to: '/settings',   icon: Settings,        label: 'Settings' },
]

export default function Sidebar() {
  const { pathname } = useLocation()
  const { signOut, user } = useAuth()
  const { transactions } = useApp()
  const uncatCount = transactions.filter(t => !t.category).length
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)

  useEffect(() => { setDrawerOpen(false); setMoreOpen(false) }, [pathname])

  useEffect(() => {
    if (drawerOpen) document.body.classList.add('drawer-open')
    else document.body.classList.remove('drawer-open')
    return () => document.body.classList.remove('drawer-open')
  }, [drawerOpen])

  const isMoreActive = MORE_ITEMS.some(i => i.to === pathname)

  return (
    <>
      {/* ══════════════════════════════════════
          DESKTOP SIDEBAR (≥769px)
      ══════════════════════════════════════ */}
      <aside className="sidebar">
        <div className="nav-logo">
          <QMark size={34} variant="teal" />
          <div>
            <div className="nav-logo-name">Quarterly</div>
            <div className="nav-logo-tag">MTD ITSA Ready</div>
          </div>
        </div>

        <nav className="nav-section">
          <div className="nav-label">Menu</div>
          {ALL_NAV.map(({ to, icon: Icon, label, badge }) => (
            <Link key={to} to={to} className={`nav-item ${pathname === to ? 'active' : ''}`}>
              <Icon size={16} />
              {label}
              {badge && uncatCount > 0 && <span className="badge-dot" />}
            </Link>
          ))}
        </nav>

        <div className="nav-footer">
          <div className="nav-user-email">{user?.email}</div>
          <button className="btn btn-ghost btn-sm btn-full" onClick={signOut}>Sign out</button>
        </div>
      </aside>

      {/* ══════════════════════════════════════
          MOBILE TOP HEADER (≤768px)
      ══════════════════════════════════════ */}
      <header className="mob-header">
        <div className="mob-header-logo">
          <QMark size={30} variant="teal" />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginLeft: 8, letterSpacing: '-.01em' }}>
            Quarterly
          </span>
          {uncatCount > 0 && (
            <span style={{ marginLeft: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)', display: 'inline-block' }} />
          )}
        </div>

        {/* Notification / account button */}
        <button
          onClick={() => setDrawerOpen(true)}
          style={{ background: 'var(--navy-border)', border: '1px solid var(--navy-border)', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          aria-label="Account menu"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--slate)" strokeWidth="2" strokeLinecap="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
      </header>

      {/* ══════════════════════════════════════
          MOBILE BOTTOM TAB BAR (≤768px)
      ══════════════════════════════════════ */}
      <nav className="mob-tabbar">
        {PRIMARY_TABS.map(({ to, icon: Icon, label, badge }) => {
          const active = pathname === to
          return (
            <Link key={to} to={to} className={`mob-tab ${active ? 'active' : ''}`}>
              {badge && uncatCount > 0 && <span className="mob-tab-dot" />}
              <Icon size={22} strokeWidth={active ? 2 : 1.6} />
              <span className="mob-tab-label">{label}</span>
            </Link>
          )
        })}

        {/* More tab */}
        <button
          className={`mob-tab ${isMoreActive ? 'active' : ''}`}
          onClick={() => setMoreOpen(o => !o)}
          aria-label="More"
        >
          <MoreHorizontal size={22} strokeWidth={isMoreActive ? 2 : 1.6} />
          <span className="mob-tab-label">More</span>
        </button>
      </nav>

      {/* ══════════════════════════════════════
          MORE SHEET (slides up from bottom)
      ══════════════════════════════════════ */}
      {moreOpen && (
        <>
          <div className="drawer-backdrop open" onClick={() => setMoreOpen(false)} />
          <div className="mob-more-sheet">
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--navy-border)', margin: '10px auto 20px' }} />
            {MORE_ITEMS.map(({ to, icon: Icon, label }) => (
              <Link key={to} to={to} className={`nav-item ${pathname === to ? 'active' : ''}`}
                style={{ fontSize: 15, padding: '14px 20px', borderRadius: 10, margin: '0 8px' }}>
                <Icon size={18} />
                {label}
              </Link>
            ))}
            <div style={{ height: 1, background: 'var(--navy-border)', margin: '12px 20px' }} />
            <div style={{ padding: '8px 20px 6px', fontSize: 11, color: 'var(--slate-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
            <button className="btn btn-ghost btn-sm" onClick={signOut}
              style={{ margin: '6px 20px 8px', width: 'calc(100% - 40px)', justifyContent: 'center' }}>
              Sign out
            </button>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════
          ACCOUNT DRAWER (slides in from right)
      ══════════════════════════════════════ */}
      <div className={`drawer-backdrop ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <div className={`mob-drawer ${drawerOpen ? 'open' : ''}`} style={{ left: 'auto', right: 0, borderRight: 'none', borderLeft: '1px solid var(--navy-border)', transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--navy-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <QMark size={28} variant="teal" />
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>Quarterly</div>
              <div style={{ fontSize: 9, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.1em' }}>MTD ITSA Ready</div>
            </div>
          </div>
          <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate)', padding: 6 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 6 }}>Signed in as</div>
          <div style={{ fontSize: 13, color: 'var(--white)', marginBottom: 20, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
          <Link to="/settings" className="btn btn-ghost btn-sm btn-full" style={{ justifyContent: 'center', marginBottom: 8 }} onClick={() => setDrawerOpen(false)}>
            Account settings
          </Link>
          <button className="btn btn-danger btn-sm btn-full" onClick={signOut} style={{ justifyContent: 'center' }}>
            Sign out
          </button>
        </div>
      </div>
    </>
  )
}
